import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { loadError, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { RegularBadge } from "@/components/GuestBadges";
import { euro, fmtDate, STATUS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Mail, Phone, Flag, CalendarDays, ArrowUpRight, Download, AlertTriangle } from "lucide-react";
import { downloadBlob } from "@/lib/download";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg bg-secondary/50 px-3 py-2">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-heading font-semibold text-base" data-testid={testId}>{value}</div>
  </div>
);

const GuestNotesEditor = ({ guest, onSaved }) => {
  const [notes, setNotes] = useState(guest.notes || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNotes(guest.notes || ""); }, [guest.key, guest.notes]);
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/guests/notes", { key: guest.key, notes });
      onSaved(r.data.notes);
      toast.success("Note salvate");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="guest-notes-editor">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-700">Note interne (preferenze, allergie…)</span>
        <Button size="sm" variant="outline" data-testid="guest-notes-save" onClick={save} disabled={saving || notes === (guest.notes || "")} className="h-7 text-xs">Salva</Button>
      </div>
      <Textarea data-testid="guest-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Es. allergia al glutine, preferisce camera silenziosa…" className="bg-card" />
    </div>
  );
};

export default function Guests() {
  const [guests, setGuests] = useState([]);
  const [q, setQ] = useState("");
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const selKey = params.get("sel");
  const selected = useMemo(() => guests.find((g) => g.key === selKey) || null, [guests, selKey]);

  useEffect(() => { api.get("/guests").then((r) => setGuests(r.data)).catch(loadError); }, []);

  const filtered = useMemo(() => guests.filter((g) => !q || `${g.guest_name} ${g.guest_email || ""} ${g.guest_phone || ""}`.toLowerCase().includes(q.toLowerCase())), [guests, q]);
  const select = (g) => setParams(g ? { sel: g.key } : {});

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Archivio</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-1">Ospiti</h1>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="guest-search" placeholder="Cerca per nome, email o telefono…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <Button variant="outline" data-testid="guests-export-csv" onClick={() => downloadBlob(`/guests/export.csv?q=${encodeURIComponent(q)}`, `ospiti_${new Date().toISOString().slice(0, 10)}.csv`, "CSV scaricato")}>
          <Download className="w-4 h-4 mr-1.5" />Esporta CSV{q ? " (filtrati)" : ""}
        </Button>
      </div>

      <Card className="border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="guests-table">
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Ospite</TableHead>
                <TableHead>Contatti</TableHead>
                <TableHead className="text-right">Soggiorni</TableHead>
                <TableHead className="text-right">Notti</TableHead>
                <TableHead className="text-right">Spesa totale</TableHead>
                <TableHead>Ultimo / Prossimo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((g) => (
                <TableRow key={g.key} data-testid={`guest-row-${g.key}`} className="cursor-pointer hover:bg-secondary/30" onClick={() => select(g)}>
                  <TableCell>
                    <div className="font-medium flex items-center gap-2 flex-wrap">{g.guest_name}<RegularBadge stays={g.stays} testId={`guest-regular-${g.key}`} className="text-[10px] py-0" />{g.allergies && <span data-testid={`guest-allergy-badge-${g.key}`} title={g.allergies} className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200 text-[10px] px-2 py-0"><AlertTriangle className="w-3 h-3" />Allergie</span>}</div>
                    <div className="text-xs text-muted-foreground">{g.nationality || "—"}</div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    <div>{g.guest_email || "—"}</div><div>{g.guest_phone || ""}</div>
                  </TableCell>
                  <TableCell className="text-right font-medium">{g.stays}</TableCell>
                  <TableCell className="text-right">{g.nights}</TableCell>
                  <TableCell className="text-right font-medium">{euro(g.total_spent)}</TableCell>
                  <TableCell className="text-xs">
                    {g.last_stay && <div>Ultimo: {fmtDate(g.last_stay)}</div>}
                    {g.next_stay && <div className="text-accent font-medium">Prossimo: {fmtDate(g.next_stay)}</div>}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Nessun ospite trovato.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!selected} onOpenChange={(v) => !v && select(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="guest-profile-dialog">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-2xl flex items-center gap-3 flex-wrap">{selected.guest_name}<RegularBadge stays={selected.stays} testId="profile-regular-badge" /></DialogTitle></DialogHeader>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
                {selected.guest_email && <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{selected.guest_email}</span>}
                {selected.guest_phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{selected.guest_phone}</span>}
                {selected.nationality && <span className="inline-flex items-center gap-1.5"><Flag className="w-3.5 h-3.5" />{selected.nationality}</span>}
                {selected.birth_date && <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" />nato/a {fmtDate(selected.birth_date)}{selected.birth_place ? ` a ${selected.birth_place}` : ""}</span>}
                {selected.document_number && <span>{selected.document_type || "Documento"} {selected.document_number}</span>}
              </div>
              {selected.allergies && (
                <div className="rounded-lg border-2 border-red-500/70 bg-red-50 dark:bg-red-900/25 px-3 py-2 text-sm flex items-start gap-2" data-testid="guest-allergies">
                  <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" /><span><b className="text-red-700 dark:text-red-200 uppercase text-xs tracking-wider">Allergie / intolleranze</b><br />{selected.allergies}</span>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3 mt-2">
                <Stat label="Soggiorni" value={selected.stays} testId="guest-stays" />
                <Stat label="Notti totali" value={selected.nights} testId="guest-nights" />
                <Stat label="Spesa totale" value={euro(selected.total_spent)} testId="guest-total" />
              </div>
              <GuestNotesEditor guest={selected} onSaved={(notes) => setGuests((gs) => gs.map((g) => (g.key === selected.key ? { ...g, notes } : g)))} />
              <Table data-testid="guest-bookings-table">
                <TableHeader>
                  <TableRow><TableHead>Soggiorno</TableHead><TableHead>Camera</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Totale</TableHead><TableHead /></TableRow>
                </TableHeader>
                <TableBody>
                  {[...selected.bookings].reverse().map((b) => {
                    const s = STATUS[b.status] || STATUS.confirmed;
                    return (
                      <TableRow key={b.id} data-testid={`guest-booking-${b.id}`} className={b.upcoming ? "bg-accent/5" : ""}>
                        <TableCell className="text-sm">{fmtDate(b.check_in)} → {fmtDate(b.check_out)}<div className="text-xs text-muted-foreground">{b.nights} notti{b.upcoming ? " · futuro" : ""}</div></TableCell>
                        <TableCell className="text-sm">{b.room_name}</TableCell>
                        <TableCell><Badge variant="secondary" className={`${s.bg} ${s.text} border-0`}>{s.label}</Badge></TableCell>
                        <TableCell className="text-right font-medium">{euro(b.total_amount)}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" data-testid={`goto-booking-${b.id}`} onClick={() => navigate(`/prenotazioni?q=${encodeURIComponent(selected.guest_name)}`)} title="Apri in Prenotazioni"><ArrowUpRight className="w-4 h-4" /></Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
